/**
 * BlogFeedStrip — auto-updating "Latest from the blog" section on the
 * Landing page. Pulls the freshest 3 posts from the blog autopilot
 * service and renders them as SEO-cited article cards with proper
 * Article schema JSON-LD.
 *
 * The blog autopilot service runs a 24-hour cadence loop, so this
 * section refreshes itself daily without any manual command.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, CalendarDays } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function BlogFeedStrip() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Try the autopilot feed first (freshest posts). If it's empty
    // (e.g. LLM budget exhausted so no auto-posts today), fall back to
    // the general blog corpus so the strip is never blank.
    (async () => {
      try {
        const r = await fetch(`${API}/api/blog-autopilot/posts?limit=3`);
        const d = r.ok ? await r.json() : { posts: [] };
        let list = d?.posts || [];
        if (list.length === 0) {
          const r2 = await fetch(`${API}/api/blog/posts?limit=3`);
          if (r2.ok) {
            const d2 = await r2.json();
            list = Array.isArray(d2) ? d2 : (d2?.posts || []);
          }
        }
        setPosts(list.slice(0, 3));
      } catch (_) {
        setPosts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Emit ItemList schema so Google can surface the strip as a related-
  // articles panel under our brand result. Removed on unmount.
  useEffect(() => {
    if (!posts.length) return undefined;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-jsonld", "blog-feed-itemlist");
    el.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Latest from the Central Rentals blog",
      "numberOfItems": posts.length,
      "itemListElement": posts.map((p, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "item": {
          "@type": "Article",
          "headline": p.title,
          "description": p.excerpt || p.meta_description || p.title,
          "datePublished": p.published_at,
          "author": { "@type": "Organization", "name": "Central Rentals Canada" },
          "url": `https://centralrentals.ca/blog/${p.slug}`,
        },
      })),
    });
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, [posts]);

  if (loading || posts.length === 0) return null;

  return (
    <section
      id="latest-from-blog"
      data-testid="blog-feed-strip"
      className="py-14 md:py-20 bg-slate-50 border-y border-slate-200"
    >
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 border border-emerald-200 text-[11px] font-extrabold tracking-[0.18em] uppercase text-emerald-800">
              <Sparkles className="w-3.5 h-3.5"/> Fresh · Auto-published daily
            </div>
            <h2 className="font-display mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Latest from the blog
            </h2>
            <p className="mt-3 text-base sm:text-lg text-slate-700 max-w-2xl">
              Canadian landlord + tenant guides, RTA changes, tax deadlines, and market updates — published every day, sourced from official government registers.
            </p>
          </div>
          <Link
            to="/blog"
            data-testid="blog-feed-see-all"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-slate-200 text-sm font-bold text-slate-800 hover:bg-slate-100"
          >
            See all posts <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="blog-feed-grid">
          {posts.map((p) => (
            <Link
              key={p.slug || p.id}
              to={`/blog/${p.slug}`}
              data-testid={`blog-feed-card-${p.slug}`}
              className="group p-6 rounded-2xl bg-white border border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all flex flex-col"
              itemScope
              itemType="https://schema.org/Article"
            >
              <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-500">
                <CalendarDays className="w-3 h-3"/>
                <time itemProp="datePublished" dateTime={p.published_at}>
                  {p.published_at ? new Date(p.published_at).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }) : ""}
                </time>
                {p.topic && <span className="text-slate-300">·</span>}
                {p.topic && <span>{p.topic}</span>}
              </div>
              <h3 itemProp="headline" className="mt-3 font-display font-extrabold text-lg text-slate-900 leading-tight group-hover:text-emerald-700">
                {p.title}
              </h3>
              {(p.excerpt || p.meta_description) && (
                <p itemProp="description" className="mt-2 text-sm text-slate-600 leading-relaxed flex-1 line-clamp-3">
                  {p.excerpt || p.meta_description}
                </p>
              )}
              <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-extrabold text-emerald-700 group-hover:text-emerald-800">
                Read the post <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform"/>
              </div>
              <meta itemProp="author" content="Central Rentals Canada"/>
              <meta itemProp="url" content={`https://centralrentals.ca/blog/${p.slug}`}/>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
